import React from "react";

import { faCheck, faPalette } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCircle, faTintSlash } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import cx from "classnames";
import { Mark, MarkType, Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, TextSelection, Transaction } from "prosemirror-state";

import { zindexRadixPopper } from "~/css/zindexes";
import { entireRangeHasMark } from "~/lib/prose/utils";
import { colors } from "~/ui";
import { Tooltip } from "~/ui";
import { IconButton } from "~/ui/button/IconButton";
import { NoteFontColors } from "~/ui/colors";
import { NoteHighlightAndFontColorsToNames } from "~/ui/colors";
import Dropdown from "~/ui/menu";
import { useSingleton } from "~/ui/Tooltip";

import { MarkExtension, type MenuEntry, type MenuEntryComponentProps } from "../extension";
import { getHighlightColourName } from "./highlight";

import styles from "~/lib/prose/css/ColourPicker.module.css";
import toolbarStyles from "~/lib/prose/plugins/toolbar/FloatingToolbar.module.css";

export function toggleMark(markType: MarkType | null, attrs: { colour: string }) {
	if (!markType) {
		throw Error(`Cannot call 'toggleMark' with an invalid mark type. Received '$type' as '${markType}'.`);
	}
	return function (state: EditorState, dispatch?: (tr: Transaction) => void) {
		const { empty, ranges } = state.selection;
		let $cursor;
		if (state.selection instanceof TextSelection) {
			({ $cursor } = state.selection);
		}

		if (dispatch) {
			let has = false;
			const tr = state.tr;

			if (empty) {
				const mark = markType.create(attrs);
				const storedMarks = tr.storedMarks ?? $cursor?.marks();
				if (!attrs.colour || (storedMarks && mark.isInSet(storedMarks))) {
					tr.removeStoredMark(markType);
				} else {
					tr.addStoredMark(mark);
				}
			} else {
				for (let i = 0; !has && i < ranges.length; i++) {
					const { $from, $to } = ranges[i];
					has = state.doc.rangeHasMark($from.pos, $to.pos, markType);
				}

				for (let i = 0; i < ranges.length; i++) {
					const { $from, $to } = ranges[i];

					if (has) {
						tr.removeMark($from.pos, $to.pos, markType);
					}

					if (attrs.colour) {
						tr.addMark($from.pos, $to.pos, markType.create(attrs));
					}
				}
			}

			dispatch(tr.scrollIntoView());
		}

		return true;
	};
}

const colourOptions = {
	dark: [
		colors.red[400],
		colors.peach[400],
		colors.yellow[400],
		colors.green[500],
		colors.royal[400],
		colors.purple[400],
	],
	light: [
		colors.red[600],
		colors.peach[600],
		colors.yellow[600],
		colors.green[600],
		colors.royal[600],
		colors.purple[600],
	],
};

export function getFontColorName(originalColour: string) {
	switch (originalColour) {
		case colors.red[600]:
		case colors.red[400]:
		case NoteFontColors.red:
			return NoteFontColors.red;
		case colors.peach[600]:
		case colors.peach[400]:
		case NoteFontColors.peach:
			return NoteFontColors.peach;
		case colors.yellow[600]:
		case colors.yellow[400]:
		case NoteFontColors.yellow:
			return NoteFontColors.yellow;
		case colors.green[600]:
		case colors.green[500]:
		case NoteFontColors.green:
			return NoteFontColors.green;
		case colors.royal[600]:
		case colors.royal[400]:
		case NoteFontColors.royal:
			return NoteFontColors.royal;
		case colors.purple[600]:
		case colors.purple[400]:
		case NoteFontColors.purple:
			return NoteFontColors.purple;
		default:
			return "";
	}
}

export default class FontMark extends MarkExtension {
	get name() {
		return "font";
	}

	get mark() {
		return {
			parseDOM: [
				{
					tag: "span",
					getAttrs: (node: HTMLElement) => {
						if (!node.classList.contains(styles.font)) return false;

						return {
							colour: node.getAttribute("data-color") || node.style.color || node.getAttribute("colour"),
						};
					},
				},
			],
			attrs: {
				colour: { default: "" },
			},
			toDOM(node: ProseMirrorNode) {
				return [
					"span",
					{
						style: `--font-colour:var(--${getFontColorName(node.attrs.colour)}); color: ${node.attrs.colour}`,
						class: styles.font,
						"data-color": node.attrs.colour,
					},
					0,
				];
			},
		};
	}

	menuEntry(): MenuEntry {
		const darkMode = document.documentElement.dataset.theme === "theme-dark";
		return {
			icon: faPalette,
			title: "Font color",
			run: (colour: string) => toggleMark(this.$type, { colour }),
			section: "colouring" as const,
			priority: 1,
			hideOnExtraSmallScreen: true,
			colourOptions: colourOptions[darkMode ? "dark" : "light"],
			component: ColourPicker,
		};
	}
}

declare module "csstype" {
	interface Properties {
		/** The selected colour to show on a note toolbar item */
		"--option-color"?: string;
	}
}

export function ColourPicker({ entry, extension, editorView, disabled, asMenuItem = false }: MenuEntryComponentProps) {
	const onSelect = React.useCallback(
		(colour: string) => {
			editorView.focus();
			entry.run(colour)(
				editorView.state,
				tr => {
					editorView.dispatch(tr);
					return true;
				},
				editorView,
			);
		},
		[entry, editorView],
	);

	/** may be a hex code or a name, depending on the mark */
	const currentColorAttr: string = React.useMemo((): string => {
		const { selection } = editorView.state;
		const { from, to, empty } = selection;
		const ext = extension.entryExtensions.get(entry);

		if (!(ext instanceof MarkExtension)) return "";
		const markType = ext.$type as MarkType;

		let mark: Mark | undefined;
		if (empty && selection instanceof TextSelection) {
			const { $cursor } = selection;
			if (!$cursor) return "";
			mark = markType?.isInSet(editorView.state.tr.storedMarks ?? $cursor.marks());
		} else {
			mark = entireRangeHasMark(from, to, markType)(editorView.state);
		}
		return mark ? mark.attrs.colour : "";
	}, [entry, editorView, extension]);

	const [tooltipSource, tooltipTarget] = useSingleton();

	const { colourOptions } = entry;
	if (!colourOptions) return null;
	const currentColorName = getFontColorName(currentColorAttr) || getHighlightColourName(currentColorAttr);

	if (asMenuItem) {
		return (
			<Dropdown.Sub>
				<Dropdown.SubTrigger icon={entry.icon} disabled={disabled}>
					{entry.title}
				</Dropdown.SubTrigger>
				<Dropdown.SubContent data-ignore-focus-lock>
					<Dropdown.RadioGroup onValueChange={onSelect} value={currentColorAttr}>
						<Dropdown.RadioItem icon={faTintSlash} value="">
							None
						</Dropdown.RadioItem>
						{colourOptions.map(colour => {
							const colorName = getFontColorName(colour) || getHighlightColourName(colour);
							return (
								<Dropdown.RadioItem
									icon={faCircle}
									iconProps={{ color: `var(--${colorName})` }}
									key={colour}
									value={colour}
								>
									{colorName ? NoteHighlightAndFontColorsToNames[colorName] : colour}
								</Dropdown.RadioItem>
							);
						})}
					</Dropdown.RadioGroup>
				</Dropdown.SubContent>
			</Dropdown.Sub>
		);
	} else {
		return (
			<Dropdown>
				{/* Singleton tooltip for the colours */}
				<Tooltip zIndex={zindexRadixPopper} singleton={tooltipSource} />
				{/* Tooltip for the trigger button */}
				<Tooltip content={entry.keyboardShortcut || entry.title} delay={500} placement="top">
					<Dropdown.Trigger
						style={{ ["--option-color"]: currentColorName ? `var(--${currentColorName})` : undefined }}
						asChild
					>
						<IconButton
							variant="tertiary"
							size="small"
							icon={entry.icon}
							className={cx(toolbarStyles.iconItem, { [styles.menuItemActiveWithColour]: Boolean(currentColorAttr) })}
							disabled={disabled}
						/>
					</Dropdown.Trigger>
				</Tooltip>
				<Dropdown.Content>
					<RadixDropdown.RadioGroup
						className={styles.horizontalGroup}
						onValueChange={onSelect}
						value={currentColorAttr}
					>
						<RadixDropdown.RadioItem value="" className={styles.horizontalItem}>
							<div className="fa-layers fa-fw">
								<FontAwesomeIcon icon={faTintSlash} style={{ fontSize: "0.875rem" }} />
							</div>
						</RadixDropdown.RadioItem>
						{colourOptions.map(color => {
							const colorName = getFontColorName(color) || getHighlightColourName(color);
							return (
								<Tooltip
									key={color}
									singleton={tooltipTarget}
									content={colorName ? NoteHighlightAndFontColorsToNames[colorName] : color}
								>
									<RadixDropdown.RadioItem className={styles.horizontalItem} value={color}>
										<div className={styles.colourSwatch} style={{ "--option-color": `var(--${colorName})` }}>
											<RadixDropdown.ItemIndicator>
												<FontAwesomeIcon icon={faCheck} transform="shrink-4" />
											</RadixDropdown.ItemIndicator>
										</div>
									</RadixDropdown.RadioItem>
								</Tooltip>
							);
						})}
					</RadixDropdown.RadioGroup>
				</Dropdown.Content>
			</Dropdown>
		);
	}
}
